// Lógica pura de "Asignar a oportunidad" (fase 2 del diseño de oportunidades): qué cambia, qué se
// valida y qué va a pasar, antes de llamar a la RPC asignar_cotizacion_a_oportunidad; y el parche
// de filas que Detalle aplica con lo que la RPC devuelve (10.234). Sin React ni red, para poder
// probarla (el proyecto no renderiza componentes en tests).

// Orden de los estatus abiertos: la regla "sube el destino" (§11.2) compara con este rango,
// igual que la RPC.
export const RANGO_ESTATUS_ABIERTO = { "50%": 1, "75%": 2, "90%": 3 };

export const NUEVA_OPORTUNIDAD = "nueva";

// Mismo criterio de nombre repetido que la RPC y el índice único de §11.1: trim + mayúsculas.
export const claveNombreOportunidad = (nombre) => (nombre || "").trim().toUpperCase();

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

/**
 * Evalúa la elección del modal.
 * @param {object} p
 * @param {object} p.cotizacion      fila de Detalle (estatus, type, id, oportunidad_id)
 * @param {Array}  p.opciones        abiertas del cliente: {oportunidad_id, nombre, estatus, n_cotizaciones}
 * @param {number|string|null} p.seleccion  id elegido o NUEVA_OPORTUNIDAD
 * @param {string} p.nombreNueva     nombre tipeado para la nueva
 * @param {string} p.nombreEditado   nombre tipeado para la elegida (renombrar)
 * @returns {{ esNueva, elegida, actual, nombreFinal, mueve, renombra, sinCambios, errorNombre, efectos, payload }}
 *   payload = argumentos para asignarCotizacionAOportunidad, o null si no hay nada válido que mandar.
 */
export const evaluarAsignacion = ({ cotizacion, opciones = [], seleccion, nombreNueva = "", nombreEditado = "" }) => {
  const actualId = cotizacion?.oportunidad_id != null ? Number(cotizacion.oportunidad_id) : null;
  const esNueva = seleccion === NUEVA_OPORTUNIDAD;
  const seleccionId = esNueva || seleccion == null ? null : Number(seleccion);
  const actual = opciones.find((o) => Number(o.oportunidad_id) === actualId) || null;
  const elegida = esNueva ? null : opciones.find((o) => Number(o.oportunidad_id) === seleccionId) || null;

  const nombreFinal = (esNueva ? nombreNueva : nombreEditado).trim();
  const mueve = esNueva || (seleccionId != null && seleccionId !== actualId);
  const renombra = !esNueva && !!elegida && nombreFinal !== "" && nombreFinal !== elegida.nombre;
  const sinCambios = !mueve && !renombra;

  let errorNombre = null;
  if (esNueva && nombreFinal === "") {
    errorNombre = "Poné un nombre para la oportunidad nueva.";
  } else if (!esNueva && elegida && nombreFinal === "") {
    errorNombre = "El nombre no puede quedar vacío.";
  } else if (esNueva || renombra) {
    const excluir = esNueva ? null : Number(elegida.oportunidad_id);
    const choca = opciones.some(
      (o) =>
        Number(o.oportunidad_id) !== excluir && claveNombreOportunidad(o.nombre) === claveNombreOportunidad(nombreFinal)
    );
    if (choca) errorNombre = `Ya hay otra oportunidad abierta de este cliente llamada "${nombreFinal}".`;
  }

  const efectos = [];
  if (cotizacion) {
    if (esNueva && nombreFinal) {
      efectos.push(`Se crea "${nombreFinal}" con esta cotización (${cotizacion.estatus}).`);
    } else if (mueve && elegida) {
      if ((RANGO_ESTATUS_ABIERTO[cotizacion.estatus] || 0) > (RANGO_ESTATUS_ABIERTO[elegida.estatus] || 0)) {
        efectos.push(
          `"${elegida.nombre}" sube de ${elegida.estatus} a ${cotizacion.estatus}, y con ella sus ${plural(
            Number(elegida.n_cotizaciones) || 0,
            "cotización",
            "cotizaciones"
          )}.`
        );
      } else if (cotizacion.estatus !== elegida.estatus) {
        efectos.push(`Esta cotización pasa a ${elegida.estatus} y toma el mes de cierre de "${elegida.nombre}".`);
      }
    }
    if (mueve && actual && Number(actual.n_cotizaciones) === 1) {
      efectos.push(`"${actual.nombre}" queda sin cotizaciones y se da de baja.`);
    }
    if (renombra) efectos.push(`"${elegida.nombre}" pasa a llamarse "${nombreFinal}".`);
  }

  const payload =
    cotizacion && !sinCambios && !errorNombre && (esNueva || elegida)
      ? {
          tipo: cotizacion.type === "contrato" ? "contrato" : "sale",
          id: cotizacion.id,
          oportunidadId: esNueva ? null : seleccionId,
          nombre: esNueva ? nombreFinal : renombra ? nombreFinal : null,
        }
      : null;

  return { esNueva, elegida, actual, nombreFinal, mueve, renombra, sinCambios, errorNombre, efectos, payload };
};

/**
 * Aplica a las filas de un tipo ('sale' → state de sales, 'contrato' → state de contratos) el
 * estado final que devolvió la RPC: oportunidad_id, estatus y mes_cierre por id. Las demás filas
 * quedan iguales (misma referencia).
 * @param {Array} filas
 * @param {Array<{tipo, id, oportunidad_id, estatus, mes_cierre}>} cotizaciones
 * @param {'sale'|'contrato'} tipo
 * @returns {Array}
 */
export const parchearFilasAsignadas = (filas, cotizaciones, tipo) => {
  const cambios = new Map((cotizaciones || []).filter((c) => c.tipo === tipo).map((c) => [Number(c.id), c]));
  if (cambios.size === 0) return filas;
  return filas.map((fila) => {
    const c = cambios.get(Number(fila.id));
    return c ? { ...fila, oportunidad_id: c.oportunidad_id, estatus: c.estatus, mes_cierre: c.mes_cierre } : fila;
  });
};
